/**
 * Product Portal — root landing page
 *
 * Presented at "/" as the entry point into the Priorities.ai platform login.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeToggle } from '../../components/ui/ThemeToggle';
import { ArrowRight, Monitor } from 'lucide-react';
import { useUserStore } from '../../stores/prioritization/userStore';

const UNKNOWN_USER_ID = 'unknown';

export default function ProductPortal() {
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);

  // Already authenticated — skip the portal and go straight to the dashboard.
  useEffect(() => {
    if (userId && userId !== UNKNOWN_USER_ID) {
      navigate('/prioritization/dashboard', { replace: true });
    }
  }, [userId, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--surface)] dark:bg-[var(--bg)] px-4 transition-colors duration-300">
      <div className="w-full max-w-2xl">

        {/* Wordmark */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-consulting-slate-blue-600 dark:bg-[var(--active-tint)] mb-5">
            <span className="text-white text-2xl font-bold leading-none">P</span>
          </div>
          <h1 className="text-2xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">
            Priorities.ai
          </h1>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Choose the experience you want to log into
          </p>
        </div>

        {/* Product cards */}
        <div className="grid grid-cols-1 gap-4">

          {/* Priorities.ai Platform */}
          <button
            onClick={() => navigate('/auth/login')}
            className="group flex flex-col text-left bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] hover:border-[var(--rule)] dark:hover:border-[var(--rule-bright)] shadow-sm dark:shadow-none p-6 transition-all duration-200 hover:shadow-md dark:hover:shadow-none cursor-pointer w-full"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="p-2.5 bg-[var(--hover-tint)] dark:bg-[var(--surface)]">
                <Monitor className="h-5 w-5 text-[var(--text-sec)] dark:text-[var(--text-sec)]" />
              </div>
              <ArrowRight className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)] group-hover:text-[var(--text-ter)] dark:group-hover:text-[var(--text-sec)] transition-colors mt-1" />
            </div>
            <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1.5">
              Priorities.ai
            </h2>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] leading-relaxed flex-1">
              The full prioritization platform. Manage items, run sessions, configure tracks, and administer your organization.
            </p>
            <div className="mt-5">
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] group-hover:gap-2.5 transition-all">
                Log in with Priorities.ai
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </button>

        </div>

        {/* Theme toggle */}
        <div className="mt-8 flex justify-center">
          <ThemeToggle variant="labeled" />
        </div>

      </div>
    </div>
  );
}
